import React from 'react';
import { Box, Text } from 'braid-design-system';
import { useAuth } from '../contexts/AuthProvider';
import { checkUserSignOnStatus } from '../services/signOnService';

const CASHIER_ROLES = new Set(['CASHIER', 'ADMIN']);

const normalizeRole = (value: string | null | undefined): string =>
    (value || '').trim().toUpperCase();

const CashierSignOnBadge: React.FC = () => {
    const { user } = useAuth();
    const canShowBadge = CASHIER_ROLES.has(normalizeRole(user?.user_role as string | null | undefined));
    const [status, setStatus] = React.useState<'loading' | 'signed-in' | 'signed-off' | 'error'>('loading');

    const refreshStatus = React.useCallback(async () => {
        if (!user?.user_name) {
            setStatus('signed-off');
            return;
        }

        try {
            const result = await checkUserSignOnStatus(user.user_name);
            setStatus(result.signon === 1 ? 'signed-in' : 'signed-off');
        } catch {
            setStatus('error');
        }
    }, [user?.user_name]);

    React.useEffect(() => {
        if (!canShowBadge) {
            return;
        }

        void refreshStatus();

        const handleRefresh = () => {
            void refreshStatus();
        };

        window.addEventListener('webpos:signon-status-changed', handleRefresh);
        return () => {
            window.removeEventListener('webpos:signon-status-changed', handleRefresh);
        };
    }, [canShowBadge, refreshStatus]);

    React.useEffect(() => {
        if (!canShowBadge) {
            return;
        }

        const timer = window.setInterval(() => {
            void refreshStatus();
        }, 30000);

        return () => window.clearInterval(timer);
    }, [canShowBadge, refreshStatus]);

    if (!canShowBadge) {
        return null;
    }

    const label =
        status === 'loading' ? 'Checking' :
        status === 'signed-in' ? 'Signed in' :
        status === 'signed-off' ? 'Signed off' :
        'Status unavailable';

    const toneClass =
        status === 'signed-in' ? 'menu-signon-badge--in' :
        status === 'signed-off' ? 'menu-signon-badge--off' :
        'menu-signon-badge--error';

    return (
        <Box className={`menu-signon-badge ${toneClass}`}>
            <span className={`menu-signon-badge-dot ${toneClass}`} />
            <Text size="small" weight="strong">
                {label}
            </Text>
        </Box>
    );
};

export default CashierSignOnBadge;